/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */
import {DragDropModule} from '@angular/cdk/drag-drop';
import {CommonModule, NgOptimizedImage} from '@angular/common';
import {NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatTreeModule} from '@angular/material/tree';
import {RouterModule} from '@angular/router';
import {NbDateFnsDateModule} from '@nebular/date-fns';
import {NbSecurityModule} from '@nebular/security';
import {
  NbActionsModule,
  NbAlertModule,
  NbAutocompleteModule,
  NbBadgeModule,
  NbButtonModule,
  NbCardModule,
  NbCheckboxModule,
  NbDatepickerModule,
  NbFormFieldModule,
  NbIconModule,
  NbInputModule,
  NbLayoutModule,
  NbListModule,
  NbMenuModule,
  NbRadioModule,
  NbSelectModule,
  NbSidebarModule,
  NbSpinnerModule,
  NbTagModule,
  NbThemeModule,
  NbTimepickerModule,
  NbToastrModule,
  NbToggleModule,
  NbTooltipModule,
  NbWindowModule
} from '@nebular/theme';
import {NgSelectModule} from '@ng-select/ng-select';
import {FormlyBootstrapModule} from '@ngx-formly/bootstrap';
import {FormlyModule} from '@ngx-formly/core';
import {FormlySelectModule} from '@ngx-formly/core/select';
import {TuiScrollable, TuiScrollbar} from '@taiga-ui/core';
import {TuiEditor, TuiEditorSocket} from '@taiga-ui/editor';
import {
  CheckboxMultiPropertyComponent,
  GridGearComponent,
  ImagePreviewComponent,
  InputFilterPropertyComponent,
  IntervalWrapperComponent,
  RadioFilterPropertyComponent,
  SelectFilterPropertyComponent,
  SharedModule,
  SortPipe
} from 'compob2b-libs';
import {TableModule} from 'compob2b-table';
import {FileUploadModule} from 'ng2-file-upload';
import {ColorPickerModule} from 'ngx-color-picker';
import {NgxJsonViewerModule} from 'ngx-json-viewer';
import {environment} from '../../environments/environment';
import {translations} from '../../environments/translations.ru';
import {ACL_OPTIONS} from '../security/security';

import {AppStateToplineComponent} from './app-state-topline/app-state-topline.component';
import {ChooseCatalogInputComponent} from './choose-catalog-input/choose-catalog-input.component';
import {ChooseCatalogModalComponent} from './choose-catalog-modal/choose-catalog-modal.component';
import {AdditionalPicturesComponent} from './crud-components/additional-pictures/additional-pictures.component';
import {CreateUpdateComponent} from './crud-components/create-update/create-update.component';
import {DocumentsUploaderComponent} from './crud-components/documents-uploader/documents-uploader.component';
import {ListActionsComponent} from './crud-components/list/list-actions/list-actions.component';
import {ListComponent} from './crud-components/list/list.component';
import {ManufacturesComponent} from './crud-components/manufactures/manufactures.component';
import {PictureComponent} from './crud-components/picture/picture.component';
import {SearchRangingRelationComponent} from './crud-components/search-ranging-relation/search-ranging-relation.component';
import {TermsByIdComponent} from './crud-components/terms-by-id/terms-by-id.component';
import {TermsComponent} from './crud-components/terms/terms.component';
import {YoutubeVideosComponent} from './crud-components/youtube-videos/youtube-videos.component';
import {AddressWithGeoComponent} from './custom-form-types/address-with-geo/address-with-geo.component';
import {AlertPropertyComponent} from './custom-form-types/alert-property/alert-property.component';
import {AutoCompleteComponent} from './custom-form-types/auto-complete/auto-complete.component';
import {CatalogPickerMultiComponent} from './custom-form-types/catalog-picker-multi/catalog-picker-multi.component';
import {CatalogPickerComponent} from './custom-form-types/catalog-picker/catalog-picker.component';

import {ColorPickerComponent} from './custom-form-types/colorpicker/color-picker.component';
import {DamCatalogPickerComponent} from './custom-form-types/dam-catalog-picker/dam-catalog-picker.component';
import {DamChooseCatalogModalComponent} from './custom-form-types/dam-choose-catalog-modal/dam-choose-catalog-modal.component';

import {DateRangePickerComponent} from './custom-form-types/date-range-picker/date-range-picker.component';
import {DateTimePickerComponent} from './custom-form-types/date-timepicker/date-timepicker.component';
import {DatepickerComponent} from './custom-form-types/datepicker/datepicker.component';
import {EditorComponent} from './custom-form-types/editor/editor.component';
import {FileUploadComponent} from './custom-form-types/file-upload/file-upload.component';

import {JsonInputComponent} from './custom-form-types/json-input/json-input.component';
import {LinkPropertyComponent} from './custom-form-types/link-property/link-property.component';
import {PhoneMaskComponent} from './custom-form-types/phone-mask/phone-mask.component';
import {ProductLinkComponent} from './custom-form-types/product-link/product-link.component';
import {SelectLazyMultiComponent} from './custom-form-types/select-lazy-multi/select-lazy-multi.component';
import {SelectLazyComponent} from './custom-form-types/select-lazy/select-lazy.component';
import {SizePropertyComponent} from './custom-form-types/size-property/size-property.component';
import {SmartlistComponent} from './custom-form-types/smartlist/smartlist.component';
import {FeatureMapperComponent} from './custom-form-types/template-mapper/modals/feature-mapper/feature-mapper.component';
import {
  TemplateFeatureValueMapperComponent
} from './custom-form-types/template-mapper/modals/template-feature-value-mapper/template-feature-value-mapper.component';
import {TemplateMapperComponent} from './custom-form-types/template-mapper/template-mapper.component';
import {ThreeStateCheckboxComponent} from './custom-form-types/three-state-checkbox/three-state-checkbox.component';
import {YoutubePreviewComponent} from './custom-form-types/youtube-preview/youtube-preview.component';
import {LayoutComponent} from './layout/layout.component';
import {PopupAddressComponent} from './modal-components/popup-address/popup-address.component';
import {ModalMenuComponent} from './modal-menu/modal-menu.component';
import {AppCreateUpdateFormComponent} from "./crud-components/create-update/app-create-update-form/app-create-update-form.component";

@NgModule({
  declarations: [
    AdditionalPicturesComponent,
    AddressWithGeoComponent,
    AlertPropertyComponent,
    AppStateToplineComponent,
    AutoCompleteComponent,
    CatalogPickerComponent,
    CatalogPickerMultiComponent,
    ChooseCatalogInputComponent,
    ChooseCatalogModalComponent,
    ColorPickerComponent,
    DamCatalogPickerComponent,
    CreateUpdateComponent,
    DocumentsUploaderComponent,
    EditorComponent,
    FeatureMapperComponent,
    FileUploadComponent,
    DamChooseCatalogModalComponent,
    JsonInputComponent,
    LayoutComponent,
    LinkPropertyComponent,
    ListComponent,
    ManufacturesComponent,
    ModalMenuComponent,
    PhoneMaskComponent,
    PictureComponent,
    PopupAddressComponent,
    ProductLinkComponent,
    SearchRangingRelationComponent,
    SelectLazyComponent,
    AppCreateUpdateFormComponent,
    SelectLazyMultiComponent,
    SizePropertyComponent,
    SmartlistComponent,
    TemplateFeatureValueMapperComponent,
    TemplateMapperComponent,
    TermsByIdComponent,
    TermsComponent,
    ThreeStateCheckboxComponent,
    YoutubePreviewComponent,
    YoutubeVideosComponent,
  ],
  imports: [
    CommonModule,
    NbLayoutModule,
    NbIconModule,
    NbActionsModule,
    NbListModule,
    SharedModule,
    TuiEditor, TuiEditorSocket,
    NgSelectModule,
    NbSecurityModule.forRoot(ACL_OPTIONS),
    NbSidebarModule.forRoot(),
    NbMenuModule.forRoot(),
    NbWindowModule.forRoot(),
    ColorPickerModule,
    NbThemeModule,
    NbSidebarModule,
    NbMenuModule,
    RouterModule,
    NbButtonModule,
    NbTooltipModule,
    ReactiveFormsModule,
    FileUploadModule,
    NbInputModule,
    NbSelectModule,
    DragDropModule,
    NbToastrModule.forRoot(),
    FormlyBootstrapModule,
    NbDatepickerModule,
    NbDatepickerModule.forRoot(),
    NbTimepickerModule.forRoot(),
    NbDateFnsDateModule.forRoot({format: environment.dateFormat}),
    FormlyModule.forRoot({
      wrappers: [
        {name: 'interval-wrapper', component: IntervalWrapperComponent},
      ],
      types: [
        {name: 'address-with-geo', component: AddressWithGeoComponent},
        {name: 'alert-property', component: AlertPropertyComponent},
        {name: 'autocomplete', component: AutoCompleteComponent},
        {name: 'catalog-picker', component: CatalogPickerComponent},
        {name: 'catalog-picker-multi', component: CatalogPickerMultiComponent},
        {name: 'color-picker-input', component: ColorPickerComponent},
        {name: 'dam-catalog-picker', component: DamCatalogPickerComponent},
        {name: 'date-range-picker', component: DateRangePickerComponent},
        {name: 'date-timepicker-input', component: DateTimePickerComponent},
        {name: 'date', component: DatepickerComponent},
        {name: 'editor-input', component: EditorComponent},
        {name: 'file-upload-input', component: FileUploadComponent},
        {name: 'input-filter-property', component: InputFilterPropertyComponent},
        {name: 'json-input', component: JsonInputComponent},
        {name: 'link-property', component: LinkPropertyComponent},
        {name: 'checkbox-multi-property', component: CheckboxMultiPropertyComponent},
        {name: 'phone-mask-input', component: PhoneMaskComponent},
        {name: 'product-link', component: ProductLinkComponent},
        {name: 'radio-filter-property', component: RadioFilterPropertyComponent},
        {name: 'select-filter-property', component: SelectFilterPropertyComponent},
        {name: 'select-lazy', component: SelectLazyComponent},
        {name: 'select-lazy-multi', component: SelectLazyMultiComponent},
        {name: 'select-multi-filter-property', component: SelectFilterPropertyComponent},
        {name: 'size-property', component: SizePropertyComponent},
        {name: 'smartlist', component: SmartlistComponent},
        {name: 'template-mapper', component: TemplateMapperComponent},
        {name: 'youtube-preview', component: YoutubePreviewComponent},
      ],
      validationMessages: [
        {name: 'required', message: translations.requiredValidationMsg}
      ]
    }),
    FormlySelectModule,
    NbCardModule,
    NbToggleModule,
    TableModule,
    NbSpinnerModule,
    NbBadgeModule,
    NbCheckboxModule,
    NbAutocompleteModule,
    NgxJsonViewerModule,
    NbRadioModule,
    NbAlertModule,
    FormsModule,
    NbTagModule,
    MatTreeModule,
    MatButtonModule,
    TuiScrollbar, TuiScrollable, GridGearComponent, ListActionsComponent, FormlyModule, NbFormFieldModule, ImagePreviewComponent, SortPipe, DatepickerComponent, NgOptimizedImage,
  ],
  exports: [
    PictureComponent,
    ListComponent,
    DamChooseCatalogModalComponent,
    AdditionalPicturesComponent,
    DocumentsUploaderComponent,
    TermsComponent,
    TermsByIdComponent,
    ManufacturesComponent,
    ProductLinkComponent,
    YoutubeVideosComponent,
    ChooseCatalogInputComponent,
    AutoCompleteComponent,
    SearchRangingRelationComponent,
    AppCreateUpdateFormComponent,
    ThreeStateCheckboxComponent,
    ModalMenuComponent,
    GridGearComponent,
    ListActionsComponent,
  ]
})
export class AdminSharedModule {
}
